import { z } from "zod";

import { ScoreTypes } from "@/enum/score.enum";
import { ERROR_TYPE } from "@/utils";

import { MetaDataSchema } from "@/types/meta-data";

import { OrganizationSchema, PeopleSchema } from "../../Apollo/schema/get-people";
import { ScoreCriteriaCriterionSchema } from "./score-criteria-criterion.schema";

const { REQUIRED } = ERROR_TYPE;

/**
 * A segment or persona name only has to be non-empty. The onboarding tour and the API accept
 * any name, and real names are short ("MVZ", "DvO") — a 4-character floor here refused to
 * save data the app had already stored (PREC-4021).
 */
const CategoryNameSchema = z.string().trim().min(1, { message: REQUIRED });

export const ScoreCompanyCategorySchema = z.object({
	id: z.number().optional(),
	description: z.string().trim().min(4, { message: REQUIRED }),
	category: CategoryNameSchema,
	type: z.string(),
	/** Switched-off rows keep their text but are left out of scoring (PREC-4055). */
	active: z.boolean().optional(),
});

export const ScoreProspectCategorySchema = z.object({
	id: z.number().optional(),
	description: z.string().trim().min(4, { message: REQUIRED }),
	category: CategoryNameSchema,
	jobsToInclude: z.array(z.string()),
	jobsToExclude: z.array(z.string()),
	investors: z.boolean(),
	active: z.boolean().optional(),

	scoreCompanyCategories: z.array(ScoreCompanyCategorySchema),
});

export const CompanyCategoryResultsSchema = z.object({
	id: z.number(),
	result: z.string(),
	reason: z.string().optional(),
	scoreCompanyCategory: ScoreCompanyCategorySchema.nullable(),
	suggestedNewSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedCategory: z
		.object({
			description: z.string(),
		})
		.optional()
		.nullable(),
});

export const TestCompanyCategoryResultsSchema = z.object({
	id: z.number(),
	result: z.string(),
	reason: z.string().optional(),
	organization: OrganizationSchema,
	scoreCompanyCategory: ScoreCompanyCategorySchema.nullable(),
	suggestedNewSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedSubCategory: z
		.object({
			name: z.string(),
			description: z.string(),
		})
		.optional()
		.nullable(),
	suggestedChangedCategory: z
		.object({
			description: z.string(),
		})
		.optional()
		.nullable(),
});

export const TestPersonaCategoryResultsSchema = z.object({
	id: z.number(),
	result: z.string(),
	reason: z.string().optional(),
	prospect: PeopleSchema,
	scorePersonaCategory: ScoreProspectCategorySchema.nullable(),
	scoreCompanyCategory: ScoreCompanyCategorySchema.nullable(),
	personaScore: z.string().nullable(),
	personaScoringSummary: z.string().nullable(),
	overallScore: z.string().nullable(),
	overallReasoning: z.string().nullable(),
	companyScore: z.string().nullable(),
	companyScoringSummary: z.string().nullable(),
});

export const ChangePersonaCategoryResultsSchema = z.object({
	id: z.number(),
	scorePersonaCategoryId: z.number().nullable().optional(),
	scoreCompanyCategoryId: z.number().nullable().optional(),
	personaResult: z.string().nullable().optional(),
	companyResult: z.string().nullable().optional(),
	overallScore: z.string().nullable().optional(),
	reason: z.string().optional(),
	fromType: z.enum(["CONTACTS", "LIST"]).optional(),
	listId: z.number().optional(),
});

export const TestPersonaCategoryResultsResponseSchema = z.object({
	data: z.array(TestPersonaCategoryResultsSchema),
	meta: MetaDataSchema,
});

export const TestCompanyCategoryResultsResponseSchema = z.array(TestCompanyCategoryResultsSchema);

export const InvestorsDescriptionSchema = z.object({
	name: z.string(),
	selected: z.boolean(),
});

export { ScoreCriteriaCriterionSchema };

export const ScoreCriteriaSchema = z.object({
	id: z.number(),
	name: z.string(),
	collapsed: z.boolean().optional(),
	criteria: ScoreCriteriaCriterionSchema,
	scoreCriteriaCompaniesPersonas: z.array(
		z.object({
			id: z.number(),
			scoreCompanyCategoryId: z.number().nullable().optional(),
			scorePersonaCategoryId: z.number(),
			investor: z.string().nullable().optional(),
		})
	),
	locked: z.boolean(),
	decisionMakerWithoutClearFunction: z.boolean(),
	updatedById: z.number().nullable(),
	updatedBy: z.object({ firstName: z.string(), lastName: z.string() }).optional().nullable(),
	updatedAt: z.string().optional().nullable(),
});

export const ScoreCriteriaDuplicateSchema = z.object({
	name: z.string(),
	criteria: ScoreCriteriaCriterionSchema,
	scoreCriteriaCompaniesPersonas: z
		.array(
			z.object({
				scoreCompanyCategoryId: z.number().nullable().optional(),
				scorePersonaCategoryId: z.number(),
				investor: z.string().nullable().optional(),
			})
		)
		.optional(),
});

export const ScoreSchema = z.object({
	id: z.number(),
	description: z.string().trim().min(4, { message: REQUIRED }).nullable(),
	partnerDescription: z.string().trim().min(4, { message: REQUIRED }).nullable(),
	competitorDescription: z.string().trim().min(4, { message: REQUIRED }).nullable(),
	serviceDescription: z.string().trim().min(4, { message: REQUIRED }).nullable(),
	configuration: z.any(),
	personaConfiguration: z.any(),
	investors: z.boolean(),
	partners: z.boolean(),
	prospectCompanyCategories: z.array(ScoreCompanyCategorySchema),
	partnerCompanyCategories: z.array(ScoreCompanyCategorySchema),
	prospectPersonaCategories: z.array(ScoreProspectCategorySchema),
	fallbackPersonaScoring: z.string().optional().nullable(),
	investorConfiguration: z.object({
		investors: z.array(InvestorsDescriptionSchema),
	}),
	decisionMakerPersonaOverwrite: ScoreProspectCategorySchema.nullable(),
	decisionMakerPersonaOverwriteId: z.number().nullable().optional(),
	companiesDone: z.boolean(),
	personasDone: z.boolean(),
	scoreDone: z.boolean(),
	migrationFlag: z.boolean(),
	language: z.string().nullable().optional(),
	event: z
		.object({
			processedAt: z.string().optional().nullable(),
			failedAt: z.string().optional().nullable(),
			publishedAt: z.string().optional().nullable(),
		})
		.optional()
		.nullable(),
	knowledgeDbChanges: z
		.object({
			hasChanges: z.boolean(),
			changes: z.array(z.any()),
			newSegmentCount: z.number(),
			removedSegmentCount: z.number(),
			changedSegmentCount: z.number(),
		})
		.optional()
		.nullable(),
});

export const ScoreCriteriaResponseSchema = z.object({
	allCriteria: z.array(ScoreCriteriaSchema),
});

/** `POST score/criteria/conflict-check` — see `LeadScoringConflictCheckResult` in shared-types. */
export const LeadScoringConflictCheckResultSchema = z.object({
	status: z.enum(["complete", "partial"]),
	conflicts: z.array(
		z.object({
			section: z.enum(["companyScoring", "personaScoring"]),
			criterion: z.string(),
			values: z.array(z.string()),
			entries: z.array(z.object({ points: z.number(), entry: z.string() })),
			source: z.enum(["table", "ai"]),
			reason: z.string().optional(),
		})
	),
});

export const CreateScoreSchema = z.object({
	name: z.string().min(4, { message: REQUIRED }).trim(),
	description: z.string().trim().optional(),
});

export const CreateScoreFromCreateListItemSchema = z.object({
	id: z.number(),
	name: z.string().optional(),
	filterParam: z.string().optional(),
	filterType: z.array(z.string()).optional(),
	selectedFilterType: z.string().nullable().optional(),
	value: z.array(z.string()).or(z.string()).or(z.number()).nullable().optional(),
	score: z.number().nullable().optional(),
});

export const ScoreCompanyPageSchema = ScoreSchema.pick({
	prospectCompanyCategories: true,
	partnerCompanyCategories: true,
	partners: true,
	competitorDescription: true,
	serviceDescription: true,
	investors: true,
	investorConfiguration: true,
	configuration: true,
}).loose();

/**
 * The persona page edits persona fields and which segments a persona is linked to. The
 * linked segments' own name/description are edited on the company page and the API only
 * reads their ids, so they are validated by id here — validating them in full made a
 * persona un-saveable because of a segment the page does not even show (PREC-4021).
 */
const LinkedSegmentSchema = ScoreCompanyCategorySchema.pick({ id: true }).loose();

export const ScoreProspectPageSchema = ScoreSchema.pick({
	prospectPersonaCategories: true,
	personaConfiguration: true,
})
	.extend({
		prospectPersonaCategories: z.array(
			ScoreProspectCategorySchema.extend({ scoreCompanyCategories: z.array(LinkedSegmentSchema) })
		),
	})
	.loose();

export const ScoreFromCreateListSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	scores: z.array(CreateScoreFromCreateListItemSchema),
	listId: z.number(),
});

export const CreateScoreFromCreateListSchema = z.object({
	name: z.string().min(4, { message: REQUIRED }).trim(),
	scores: z.array(CreateScoreFromCreateListItemSchema),
});

export const UpdateScoreSchema = z.object({
	id: z.number(),
	name: z.string().min(4, { message: REQUIRED }).trim(),
	description: z.string().trim().optional(),
	filters: z
		.record(
			z.nativeEnum(ScoreTypes),
			z.array(
				z.object({
					value: z.array(z.string()).nullable(),
					valueFilter: z.array(z.object({ id: z.number(), name: z.string() })).nullable(),
					valueNumeric: z.number().nullable(),
					valueNumericTill: z.number().nullable(),
					scoreValue: z.number().nullable(),
				})
			)
		)
		.nullable(),
});
export type { ScoreApiResponse } from "./score-api-response.types";

export type Score = z.infer<typeof ScoreSchema>;

export type CreateScore = z.infer<typeof CreateScoreSchema>;
export type UpdateScore = z.infer<typeof UpdateScoreSchema>;

export type ScoreResponse = z.infer<typeof ScoreSchema>;
export type TestCompanyCategoryResultsResponse = z.infer<
	typeof TestCompanyCategoryResultsResponseSchema
>;

export type TestCompanyCategoryResults = z.infer<typeof TestCompanyCategoryResultsSchema>;

export type TestPersonaCategoryResults = z.infer<typeof TestPersonaCategoryResultsSchema>;

export type TestPersonaCategoryResultsResponse = z.infer<
	typeof TestPersonaCategoryResultsResponseSchema
>;

export type CreateScoreFromCreateList = z.infer<typeof CreateScoreFromCreateListSchema>;
export type ScoreFromCreateList = z.infer<typeof ScoreFromCreateListSchema>;

export type ScoreCriteriaCriterion = z.infer<typeof ScoreCriteriaCriterionSchema>;
export type ScoreCriteriaType = z.infer<typeof ScoreCriteriaSchema>;
export type ScoreCriteriaResponse = z.infer<typeof ScoreCriteriaResponseSchema>;

export type ScoreCriteriaDuplicate = z.infer<typeof ScoreCriteriaDuplicateSchema>;

export type ChangePersonaCategoryResults = z.infer<typeof ChangePersonaCategoryResultsSchema>;

// History schemas

const HistoryUserSchema = z.object({
	id: z.number(),
	firstName: z.string(),
	lastName: z.string(),
});

export const CompanyCategoryResultHistoryEntrySchema = z.object({
	id: z.number(),
	scoreCompanyCategoryResultId: z.number(),
	previousResult: z.string().nullable(),
	newResult: z.string(),
	reason: z.string().optional(),
	userId: z.number(),
	user: HistoryUserSchema,
	prospect: z
		.object({
			id: z.number(),
			name: z.string(),
		})
		.nullable()
		.optional(),
	scoreCompanyCategoryResult: z
		.object({
			id: z.number(),
			result: z.string(),
			companyCategory: z.string().nullable().optional(),
			organizationId: z.number(),
			organization: z
				.object({
					id: z.number(),
					name: z.string(),
				})
				.nullable()
				.optional(),
		})
		.nullable()
		.optional(),
	createdAt: z.string(),
});

export const CompanyCategoryResultHistoryResponseSchema = z.object({
	data: z.array(CompanyCategoryResultHistoryEntrySchema),
	meta: MetaDataSchema,
});

export const PersonaCategoryResultHistoryEntrySchema = z.object({
	id: z.number(),
	scorePersonaCategoryResultId: z.number(),
	previousResult: z.string().nullable(),
	newResult: z.string(),
	previousOverallScore: z.string().nullable().optional(),
	newOverallScore: z.string().nullable().optional(),
	reason: z.string().optional(),
	userId: z.number(),
	user: HistoryUserSchema,
	scorePersonaCategoryResult: z
		.object({
			id: z.number(),
			result: z.string(),
			prospectId: z.number(),
			prospect: z
				.object({
					id: z.number(),
					name: z.string(),
					organization: z
						.object({
							id: z.number(),
							name: z.string(),
						})
						.nullable()
						.optional(),
				})
				.nullable()
				.optional(),
		})
		.nullable()
		.optional(),
	createdAt: z.string(),
});

export const PersonaCategoryResultHistoryResponseSchema = z.object({
	data: z.array(PersonaCategoryResultHistoryEntrySchema),
	meta: MetaDataSchema,
});

export const ScoringHistoryEntrySchema = z.object({
	type: z.enum(["company", "persona"]),
	id: z.number(),
	previousResult: z.string().nullable(),
	newResult: z.string(),
	previousOverallScore: z.string().nullable().optional(),
	newOverallScore: z.string().nullable().optional(),
	reason: z.string().optional(),
	createdAt: z.string(),
	userId: z.number(),
	userFirstName: z.string(),
	userLastName: z.string(),
	prospectId: z.number().nullable(),
	prospectName: z.string().nullable(),
	organizationId: z.number().nullable(),
	organizationName: z.string().nullable(),
	fromType: z.string().nullable().optional(),
	listId: z.number().nullable().optional(),
	listName: z.string().optional(),
});

export const ScoringHistoryResponseSchema = z.object({
	data: z.array(ScoringHistoryEntrySchema),
	meta: MetaDataSchema,
});

export type ScoringHistoryEntry = z.infer<typeof ScoringHistoryEntrySchema>;

// export const ScoringHistoryResponseSchema = z.object({
// 	companyHistory: z.object({
// 		data: z.array(CompanyCategoryResultHistoryEntrySchema),
// 		meta: MetaDataSchema,
// 	}),
// 	personaHistory: z.object({
// 		data: z.array(PersonaCategoryResultHistoryEntrySchema),
// 		meta: MetaDataSchema,
// 	}),
// });

export type CompanyCategoryResultHistoryEntry = z.infer<
	typeof CompanyCategoryResultHistoryEntrySchema
>;
export type CompanyCategoryResultHistoryResponse = z.infer<
	typeof CompanyCategoryResultHistoryResponseSchema
>;
export type PersonaCategoryResultHistoryEntry = z.infer<
	typeof PersonaCategoryResultHistoryEntrySchema
>;
export type PersonaCategoryResultHistoryResponse = z.infer<
	typeof PersonaCategoryResultHistoryResponseSchema
>;
export type ScoringHistoryResponse = z.infer<typeof ScoringHistoryResponseSchema>;
